/* Copyright (c) 2026 Health Endeavors, LLC. All Rights Reserved */

import styles from './ExternalReviewerView.module.scss';
import { useReviewParamsToFetchUnAuthDocumentVersion } from './useReviewParamsToFetchUnAuthDocumentVersion';
import {
  selectMessages,
  setDocumentToSearchFor,
  textInputThunk,
  toggleOptionKey,
} from '../features/sharedcomponents/chat/chatSlice';
import { useAppDispatch, useAppSelector } from '../app/hooks';
import { AudienceType, MessageType } from '../features/sharedcomponents/algorithm/types';
import { AudienceFilterProvider } from '../features/sharedcomponents/chat/hooks/ChatContext';
import { Message } from '../features/sharedcomponents/chat/Message';
import { ReviewCommentContainer } from './Comments';
import { useSearchParams } from 'react-router-dom';
import { Fragment, useEffect, useRef, useState } from 'react';
import findLastIndex from 'lodash/findLastIndex';
import { setCurrentSearchInReview } from '../features/Comments/commentsSlice';
import { fetchDocumentForExternalReviewThunk } from './externalReviewSlice';

/*Make this one a chatComponent like wrapper. Only , this is unauthenticated.
 Directly renders messages because we don't need the Audience or TopLayout here.
 The component mostly replicates existing behaviour from ChatComponent, Chat.*/
export function ExternalReviewerView() {
  const messages = useAppSelector(selectMessages);
  const noAudienceFilter = {} as AudienceType;
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();
  const divRef = useRef(null);
  const reviewId = searchParams.get('consultant-reviews') || '';
  const lastMessageRef = useRef<MessageType | undefined>(messages[messages.length - 1]);
  const lastMessage = messages[messages.length - 1];
  const currentSearchInReview = useAppSelector((state) => state.comments.currentSearchInReview);
  const [error, setError] = useState<string>('');
  const [canAddComments, setCanAddComments] = useState(true);
  const [isDataBeingFetched, setIsDataBeingFetched] = useState(false);
  useReviewParamsToFetchUnAuthDocumentVersion();
  useEffect(() => {
    // cleanup for setCurrentSearchInReview as we move away.
    return () => {
      dispatch(setCurrentSearchInReview(undefined));
    };
  }, [dispatch]);
  useEffect(() => {
    /* 'smooth' looks nice, but behaviour is a bit buggy */
    // This is buggy in Microsoft Teams
    if (lastMessageRef.current?.pages?.[0] !== messages[messages.length - 1]?.pages?.[0]) {
      (divRef as any)?.current?.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'nearest' });
      lastMessageRef.current = messages[messages.length - 1];
    }
  }, [messages]);
  const messageIndexToScrollTo = findLastIndex(
    messages,
    (m) => !!m.is_sent && !(m.extra && m.extra.type === 'OPEN_LINK' && m.hide_sent)
  );
  const scrollToDiv = (
    <div className={styles.scrollToDiv}>
      <div key="divref" ref={divRef} id="lastMessageToScrollTo" />
    </div>
  );
  const external_link = lastMessage?.extra?.type === 'OPEN_LINK' ? lastMessage?.extra?.external_link : undefined;
  if (external_link?.link && external_link?.link[0] !== '#') {
    // Sanity check
    throw new Error('external_link.link must start with a #');
  }
  useEffect(() => {
    // Set the document to search for with external link's slug and text
    if (external_link) {
      dispatch(
        setCurrentSearchInReview({
          audience: noAudienceFilter,
          text: external_link?.link?.slice(1), // Remove the leading #
          slug: external_link?.link_slug,
          publishedOnly: false,
          searchCategories: true,
          keep: true,
          howActivated: 'algorithmClick',
        })
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [external_link]);
  /* When we initially load the document, we hit an unauthenticated api that returns the documentVersion with the link info.
  fetchDocumentForExternalReviewThunk.fulfilled however sets the documentVersion in chatSlice because the "<Message/>" fetches data from chatSlice.
  When we hit an externalLink we just use are existing textInputThunk as the response would be same regardless of authentication. */
  useEffect(() => {
    const fetchData = async () => {
      setIsDataBeingFetched(true);

      try {
        if (currentSearchInReview?.documentVersionId) {
          const { consultant_reviewer_info } = await dispatch(
            fetchDocumentForExternalReviewThunk({
              doc_version_id: currentSearchInReview.documentVersionId,
              review_id: reviewId,
            })
          ).unwrap();

          setError('');
          setCanAddComments(consultant_reviewer_info.can_comment ?? true);
        }

        if (currentSearchInReview?.text || currentSearchInReview?.slug) {
          void dispatch(
            setDocumentToSearchFor({
              audience: noAudienceFilter,
              howActivated: 'algorithmClick',
              publishedOnly: false,
              searchCategories: true,
              slug: currentSearchInReview?.slug,
              text: currentSearchInReview?.text,
            })
          );
          void dispatch(textInputThunk(currentSearchInReview));
        }
      } catch (err) {
        console.error('Failed to fetch document for review:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch document for review');
      } finally {
        setIsDataBeingFetched(false);
      }
    };

    void fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSearchInReview, reviewId]);
  /* Rendering the messages directly to avoid modifying the chat or the message components.
     We just pass the reviewcommentsChild as the 'commentsChild'.*/
  return (
    <div className={styles.container}>
      {error ? (
        <p>
          <i>{error}</i>
        </p>
      ) : (
        <AudienceFilterProvider value={noAudienceFilter}>
          <div className={styles.messageAndVote}>
            {messages.map((message, messageIndex) => (
              <>
                {messageIndex === messageIndexToScrollTo && scrollToDiv}
                {message.extra?.type !== 'MATCH_FOUND' && (
                  <Message
                    message={message}
                    onKeyToggled={(page, key, value) => dispatch(toggleOptionKey({ page, messageIndex, key, value }))}
                    allowPageChangeRoot={false}
                    allowPageDelete={false}
                    isLastMessage={messageIndex === messages.length - 1}
                    commentsChild={
                      canAddComments && !isDataBeingFetched ? (
                        <div className={styles.votingDiv}>
                          <ReviewCommentContainer reviewId={reviewId} message={message} />
                        </div>
                      ) : undefined
                    }
                    inAlgorithmView={false}
                    compact={true}
                  />
                )}
              </>
            ))}
          </div>
        </AudienceFilterProvider>
      )}
    </div>
  );
}
